import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Bell,
  Inbox,
  Settings as SettingsIcon,
  Smartphone,
} from "lucide-react";
import MyNotificationsTab from "./tabs/MyNotificationsTab";
import PreferencesTab from "./tabs/PreferencesTab";
import PushSubscribersTab from "./tabs/PushSubscribersTab";

/**
 * Notification Management — single page with a horizontal tab strip and the
 * active tab body underneath. Same shell every tab renders into.
 *
 * Tabs:
 *   - My Notifications  → history list with search, filters, pagination, bulk.
 *   - Preferences       → master enable + channel toggles + per-event matrix.
 *   - Push Subscribers  → enable/disable push on this device + all subscribers.
 *
 * Active tab is reflected in the URL (`?tab=history`) so deep-links and
 * browser back/forward work and the sidebar Notifications link lands on a
 * sensible default.
 */
type TabId = "history" | "preferences" | "subscribers";

interface Tab {
  id: TabId;
  label: string;
  icon: JSX.Element;
}

const TABS: Tab[] = [
  {
    id: "history",
    label: "My Notifications",
    icon: <Inbox className="w-4 h-4" />,
  },
  {
    id: "preferences",
    label: "Preferences",
    icon: <SettingsIcon className="w-4 h-4" />,
  },
  {
    id: "subscribers",
    label: "Push Subscribers",
    icon: <Smartphone className="w-4 h-4" />,
  },
];

function NotificationManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = (searchParams.get("tab") as TabId) || "history";
  const [activeTab, setActiveTab] = useState<TabId>(
    TABS.find((t) => t.id === initial) ? initial : "history",
  );

  // Pick up changes to ?tab=… from in-app navigation.
  useEffect(() => {
    const t = searchParams.get("tab") as TabId | null;
    if (t && TABS.find((x) => x.id === t) && t !== activeTab) {
      setActiveTab(t);
    }
  }, [searchParams]);

  const selectTab = (id: TabId) => {
    setActiveTab(id);
    setSearchParams((p) => {
      const next = new URLSearchParams(p);
      next.set("tab", id);
      return next;
    });
  };

  return (
    <div className="min-h-[calc(100vh-7rem)] bg-slate-50">
      {/* Header — title strip + horizontal tab nav underneath */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-6 pt-4">
          <div className="flex items-center gap-3 pb-3">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
              <Bell className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">
                Notification Management
              </h1>
              <p className="text-xs text-slate-600">
                Manage everything DocxIQ pings you about — history, channels and
                devices.
              </p>
            </div>
          </div>

          {/* Horizontal tabs */}
          <nav className="flex gap-1">
            {TABS.map((t) => {
              const isActive = t.id === activeTab;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => selectTab(t.id)}
                  className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "text-blue-600"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <span
                    className={isActive ? "text-blue-600" : "text-slate-500"}
                  >
                    {t.icon}
                  </span>
                  <span>{t.label}</span>
                  {isActive && (
                    <span className="absolute -bottom-px left-2 right-2 h-0.5 bg-blue-600 rounded-t-full" />
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Active tab body */}
      <main className="py-5">
        <div className="bg-white border border-slate-200 rounded-md p-5">
          {activeTab === "history" && <MyNotificationsTab />}
          {activeTab === "preferences" && <PreferencesTab />}
          {activeTab === "subscribers" && <PushSubscribersTab />}
        </div>
      </main>
    </div>
  );
}

export default NotificationManagement;
